import React, {FC, useCallback} from 'react';

import {PaletteColorsEnum} from '../../../../../../libs/shared/core/src/models/api/paletteModel';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {
  HighlightModel,
  SearchActivityModel,
  SearchActivityTypesEnum,
} from '../../../../../models/api/conversationHighlightModel';
import {Highlight} from '../../header/search/highlight/highlight';
import {CellCommentLine, CellCommentLineRenderSummaryData, renderSummaryText} from './cellCommentLine';
import {CellSummary} from './cellSummary';

/*
 * Constants.
 */

const emptyStringStartRegex = /^\s+/;
const emptyStringEndRegex = /\s+$/;

export interface VisualSearchActivityModel extends SearchActivityModel {
  author?: TeammateModel;
}

/*
 * Props.
 */

interface CellSearchActivitySummaryHighlightProps {
  isSelected?: boolean;
  summaryHighlight: HighlightModel;
  searchActivity?: VisualSearchActivityModel;
  mentionableAliases?: ReadonlySet<string>;
  isCompact?: boolean;
}

/*
 * Component.
 */

export const CellSearchActivitySummaryHighlight: FC<CellSearchActivitySummaryHighlightProps> = (props) => {
  const {isSelected, searchActivity, summaryHighlight, mentionableAliases, isCompact} = props;

  const renderSummary = useCallback(
    (data: CellCommentLineRenderSummaryData) => {
      // Interpret markdown in highlighted comments.
      const highlightPartRenderer = (part: string) => {
        // Highlights easily create unterminated code blocks. Remove distracting backquotes.
        const textWithoutBackquotes = part.replaceAll('`', '');
        const markdownParsedText = renderSummaryText(
          textWithoutBackquotes,
          data.emojiValidationMap,
          isSelected,
          mentionableAliases,
        );
        // The markdown renderer trims whitespaces, so we need to add them back.
        return (
          <>
            {emptyStringStartRegex.test(part) && ' '}
            {markdownParsedText}
            {emptyStringEndRegex.test(part) && ' '}
          </>
        );
      };

      return (
        <Highlight
          text={data.props.text}
          ranges={summaryHighlight.ranges}
          highlightPartRenderer={highlightPartRenderer}
          backgroundColor={PaletteColorsEnum.YELLOW}
          size={VisualSizesEnum.SMALL}
          isParentSelected={isSelected}
          hideWhitespaceOnly={isCompact}
        />
      );
    },
    [isCompact, isSelected, mentionableAliases, summaryHighlight.ranges],
  );

  // If we matched on a comment, try to display the highlight as a comment.
  if (searchActivity?.visualType === SearchActivityTypesEnum.COMMENT && searchActivity?.author) {
    return (
      <CellCommentLine
        isSelected={isSelected}
        text={summaryHighlight.text}
        annotation={undefined}
        renderSummary={renderSummary}
        shouldDisplayAttachmentIcon={false}
        shouldUseEllipsisOverflow={false}
        author={searchActivity.author}
        mentionableAliases={mentionableAliases}
        hasHighlight
        isCompact={isCompact}
      />
    );
  }

  return (
    <CellSummary isSelected={isSelected} highlight={summaryHighlight} type={searchActivity?.visualType} />
  );
};
